import { useEffect, useState } from "react";
import { onSnapshot, orderBy, query } from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import { transactionSchema, type Transaction } from "@/lib/schemas";

const cache = new Map<string, Transaction[]>();

/**
 * Wszystkie transactions w household (earn + payout + adjustment),
 * sortowane po createdAt desc. Realtime przez onSnapshot.
 *
 * Dla parent wallet: pozwala policzyć per-(child, funder) balance
 * po jednym fetchu. Limit nie jest narzucony — przy 1000+ w aktywnej
 * rodzinie warto będzie kiedyś dodać paginację albo separate aggregate.
 */
export function useHouseholdTransactions(hid: string | undefined): {
  transactions: Transaction[];
  loading: boolean;
} {
  const cached = hid ? cache.get(hid) : undefined;
  const [state, setState] = useState<{
    transactions: Transaction[];
    loading: boolean;
  }>({
    transactions: cached ?? [],
    loading: cached === undefined,
  });

  useEffect(() => {
    if (!hid) return;
    const db = getDb();
    const q = query(
      refCol(db, paths.transactions(hid)),
      orderBy("createdAt", "desc"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const transactions = snap.docs.flatMap((d) => {
          const parsed = transactionSchema.safeParse({ id: d.id, ...d.data() });
          return parsed.success ? [parsed.data] : [];
        });
        cache.set(hid, transactions);
        setState({ transactions, loading: false });
      },
      () => setState({ transactions: [], loading: false }),
    );
    return () => unsub();
  }, [hid]);

  return state;
}
